import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useQueryClient } from "@tanstack/react-query";
import { 
  mapDisputeTypeApiToUi, 
  mapDisputeTypeUiToCreateRequest,
  mapDisputeTypeUiToUpdateRequest,
} from "@/lib/mappers/dispute-types";
import { generateMockDisputeTypes } from "@/lib/mock-data/dispute-types-mock-data";
import type { DisputeType } from "@/types/dispute-types";
import type { DisputeTypeApiResponse } from "@/types/api/dispute-types";

const mockDisputeTypes = generateMockDisputeTypes();

export function useDisputeTypesList() {
  const { apiGet } = useApi();

  return useModeAwareQuery<DisputeType[], Error>(
    ["dispute-types"],
    async ({ signal }) => {
      const response = await apiGet<DisputeTypeApiResponse[]>("/api/v1/settings/dispute-types", undefined, signal);
      return response.map(mapDisputeTypeApiToUi);
    },
    mockDisputeTypes,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useDisputeTypeById(disputeTypeId: string | null) {
  const { apiGet } = useApi();
  const mockDisputeType = mockDisputeTypes.find(x => x.id === disputeTypeId) ?? null;

  return useModeAwareQuery<DisputeType | null, Error>(
    ["dispute-type", disputeTypeId],
    async ({ signal }) => {
      if (!disputeTypeId) return null;
      const response = await apiGet<DisputeTypeApiResponse>(`/api/v1/settings/dispute-types/${disputeTypeId}`, undefined, signal);
      return mapDisputeTypeApiToUi(response);
    },
    mockDisputeType,
    {
      enabled: !!disputeTypeId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useCreateDisputeType() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<DisputeTypeApiResponse, Error, Partial<DisputeType>>(
    async (data) => {
      const request = mapDisputeTypeUiToCreateRequest(data);
      return apiPost<DisputeTypeApiResponse>("/api/v1/settings/dispute-types", request);
    },
    async (data) => {
      return {
        id: Date.now(),
        name: data.name ?? "New Dispute Type",
        description: data.description,
        isActive: data.isActive ?? true,
        usageCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types"] });
      },
    }
  );
}

export function useUpdateDisputeType(disputeTypeId: string) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<DisputeTypeApiResponse, Error, Partial<DisputeType>>(
    async (data) => {
      const request = mapDisputeTypeUiToUpdateRequest(data);
      return apiPut<DisputeTypeApiResponse>(`/api/v1/settings/dispute-types/${disputeTypeId}`, request);
    },
    async (data) => {
      return {
        id: Number(disputeTypeId),
        name: data.name ?? "Updated Dispute Type",
        description: data.description,
        isActive: data.isActive ?? true,
        usageCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types"] });
        queryClient.invalidateQueries({ queryKey: ["dispute-type", disputeTypeId] });
      },
    }
  );
}

export function useDeleteDisputeType() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, string>(
    async (disputeTypeId) => {
      await apiDelete<void>(`/api/v1/settings/dispute-types/${disputeTypeId}`);
    },
    async () => {
      return;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types"] });
      },
    }
  );
}

export function useToggleDisputeTypeStatus() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<DisputeTypeApiResponse, Error, { id: string; isActive: boolean }>(
    async (data) => {
      return apiPut<DisputeTypeApiResponse>(`/api/v1/settings/dispute-types/${data.id}`, { isActive: data.isActive });
    },
    async (data) => {
      return {
        id: Number(data.id),
        name: "Dispute Type",
        isActive: data.isActive,
        usageCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ["dispute-types"] });
        queryClient.invalidateQueries({ queryKey: ["dispute-type", variables.id] });
      },
    }
  );
}
